import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Mail,
  Phone,
  Calendar,
  ShoppingBag,
  Coins,
  User,
} from 'lucide-react';
import { apiGet } from '../lib/api';
import { formatNTD, formatDate } from '@yiweiwu/shared';
import type { MemberDetail as MemberDetailType } from '../lib/types';
import { StatusBadge } from '../components/StatusBadge';

export default function MemberDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [member, setMember] = useState<MemberDetailType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchMember = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError('');
      const result = await apiGet<MemberDetailType>(`/members/${id}`);
      setMember(result);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : '載入會員資料失敗');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchMember();
  }, [fetchMember]);

  if (loading) {
    return (
      <div className="p-12 text-center">
        <div className="inline-block animate-spin rounded-full h-10 w-10 border-4 border-brand-bake border-t-brand-gold" />
        <p className="mt-4 text-gray-400">載入會員資料...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8 text-center">
        <p className="text-red-500 mb-4">{error}</p>
        <button
          onClick={fetchMember}
          className="px-4 py-2 rounded-lg bg-brand-gold text-white text-sm hover:bg-brand-gold/90"
        >
          重新載入
        </button>
      </div>
    );
  }

  if (!member) return null;

  return (
    <div>
      {/* 頁首 */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate('/admin/members')}
          className="p-2 rounded-lg hover:bg-brand-bake/50 transition-colors"
        >
          <ArrowLeft className="h-5 w-5 text-gray-500" />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-full bg-brand-gold/20 flex items-center justify-center">
              <User className="h-6 w-6 text-brand-gold" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-brand-cocoa font-heading">
                {member.name}
              </h2>
              <p className="text-sm text-gray-500">
                加入於 {formatDate(member.createdAt)}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* 左欄：基本資料與偏好 */}
        <div className="lg:col-span-2 space-y-6">
          {/* 基本資料 */}
          <div className="bg-white rounded-xl border border-brand-bake p-5 shadow-sm">
            <h3 className="text-base font-semibold text-brand-cocoa mb-4">
              基本資料
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-gray-400 flex-shrink-0" />
                <span className="text-gray-500">Email：</span>
                <span className="font-medium">{member.email ?? '未設定'}</span>
              </div>
              <div className="flex items-center gap-2">
                <Phone className="h-4 w-4 text-gray-400 flex-shrink-0" />
                <span className="text-gray-500">電話：</span>
                <span className="font-medium">{member.phone ?? '未設定'}</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-gray-400 flex-shrink-0" />
                <span className="text-gray-500">加入日期：</span>
                <span className="font-medium">{formatDate(member.createdAt)}</span>
              </div>
            </div>
          </div>

          {/* 統計 */}
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-white rounded-xl border border-brand-bake p-4 shadow-sm text-center">
              <ShoppingBag className="h-5 w-5 text-brand-gold mx-auto mb-1" />
              <p className="text-2xl font-bold text-brand-caramel font-heading">
                {member.stats.orderCount}
              </p>
              <p className="text-xs text-gray-500">總訂單數</p>
            </div>
            <div className="bg-white rounded-xl border border-brand-bake p-4 shadow-sm text-center">
              <Coins className="h-5 w-5 text-brand-gold mx-auto mb-1" />
              <p className="text-2xl font-bold text-brand-caramel font-heading">
                {member.stats.totalSpent > 0
                  ? formatNTD(member.stats.totalSpent)
                  : '--'}
              </p>
              <p className="text-xs text-gray-500">總消費</p>
            </div>
            <div className="bg-white rounded-xl border border-brand-bake p-4 shadow-sm text-center">
              <Coins className="h-5 w-5 text-brand-gold mx-auto mb-1" />
              <p className="text-2xl font-bold text-brand-caramel font-heading">
                {member.points}
              </p>
              <p className="text-xs text-gray-500">現有點數</p>
            </div>
          </div>

          {/* 顧客偏好 */}
          {member.profile && (
            <div className="bg-white rounded-xl border border-brand-bake p-5 shadow-sm">
              <h3 className="text-base font-semibold text-brand-cocoa mb-4">
                偏好與備註
              </h3>
              <div className="space-y-3 text-sm">
                {member.profile.allergens && (
                  <div>
                    <span className="text-gray-500">過敏原：</span>
                    <span className="font-medium text-status-error">
                      {member.profile.allergens}
                    </span>
                  </div>
                )}
                {member.profile.tastePreferences && (
                  <div>
                    <span className="text-gray-500">口味偏好：</span>
                    <span>{member.profile.tastePreferences}</span>
                  </div>
                )}
                {member.profile.coffeePreferences && (
                  <div>
                    <span className="text-gray-500">咖啡偏好：</span>
                    <span>{member.profile.coffeePreferences}</span>
                  </div>
                )}
                {member.profile.giftNeeds && (
                  <div>
                    <span className="text-gray-500">送禮需求：</span>
                    <span>{member.profile.giftNeeds}</span>
                  </div>
                )}
                {member.profile.internalNotes && (
                  <div className="pt-3 border-t border-brand-bake/50">
                    <span className="text-gray-500">內部備註：</span>
                    <p className="mt-1 p-3 bg-brand-cream rounded-lg text-gray-600">
                      {member.profile.internalNotes}
                    </p>
                  </div>
                )}
                {!member.profile.allergens &&
                  !member.profile.tastePreferences &&
                  !member.profile.coffeePreferences &&
                  !member.profile.giftNeeds &&
                  !member.profile.internalNotes && (
                    <p className="text-gray-400">尚無偏好資料</p>
                  )}
              </div>
            </div>
          )}

          {/* 最近訂單 */}
          <div className="bg-white rounded-xl border border-brand-bake p-5 shadow-sm">
            <h3 className="text-base font-semibold text-brand-cocoa mb-4">
              最近訂單
            </h3>
            {member.recentOrders.length === 0 ? (
              <p className="text-sm text-gray-400">尚無訂單記錄</p>
            ) : (
              <div className="divide-y divide-brand-bake/50">
                {member.recentOrders.map((order) => (
                  <div
                    key={order.id}
                    className="flex items-center justify-between py-3 first:pt-0 last:pb-0"
                  >
                    <div>
                      <button
                        onClick={() =>
                          navigate(`/admin/orders/${order.id}`)
                        }
                        className="font-mono text-sm text-brand-gold hover:underline"
                      >
                        {order.orderNumber}
                      </button>
                      <p className="text-xs text-gray-400">
                        {formatDate(order.createdAt)}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <StatusBadge status={order.status} />
                      <span className="text-sm font-medium text-gray-900 min-w-[80px] text-right">
                        {formatNTD(order.total)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* 右欄：點數歷史 */}
        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-brand-bake p-5 shadow-sm">
            <h3 className="text-base font-semibold text-brand-cocoa mb-4">
              點數異動記錄
            </h3>
            {member.pointsHistory.length === 0 ? (
              <p className="text-sm text-gray-400">尚無點數記錄</p>
            ) : (
              <div className="space-y-3">
                {member.pointsHistory.map((entry) => (
                  <div
                    key={entry.id}
                    className="flex items-start justify-between py-2 border-b border-brand-bake/30 last:border-0"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-700">
                        {entry.type === 'earn'
                          ? '獲得點數'
                          : entry.type === 'redeem'
                            ? '兌換點數'
                            : entry.type}
                      </p>
                      {entry.note && (
                        <p className="text-xs text-gray-400 mt-0.5">
                          {entry.note}
                        </p>
                      )}
                      <p className="text-xs text-gray-400">
                        {formatDate(entry.createdAt, {
                          month: '2-digit',
                          day: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </p>
                    </div>
                    <div className="ml-3 text-right flex-shrink-0">
                      <p
                        className={`text-sm font-medium ${
                          entry.points > 0
                            ? 'text-status-success'
                            : 'text-status-error'
                        }`}
                      >
                        {entry.points > 0
                          ? `+${entry.points}`
                          : `${entry.points}`}
                      </p>
                      <p className="text-xs text-gray-400">
                        餘額 {entry.balanceAfter}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
